import React from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import styles from './TermsPage.module.css';

const TermsPage = () => {
  usePageMeta({
    title: 'Terms',
    path: '/terms',
    description: 'DarkNews terms of service — aggregation, outbound originals, and copyright / DMCA path.',
  });
  return (
    <div className={styles.termsPage}>
      <div className="container">
        <div className={styles.pageHeader}>
          <h1>Terms of Service</h1>
          <p className={styles.lastUpdated}>Last updated: August 17, 2026</p>
        </div>

        <div className={styles.content}>
          <section className={styles.section}>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using DarkNews ("the Site"), you accept and agree to be bound 
              by the terms and provision of this agreement. If you do not agree to abide by the 
              above, please do not use this service.
            </p>
          </section>

          <section className={styles.section}>
            <h2>2. Description of Service</h2>
            <p>
              DarkNews is an AI-powered news aggregation platform. We harvest RSS feeds,
              show headlines and RSS descriptions, and link out to original publishers. We use
              AI for search, analysis, and research — not as a substitute for the source. Our
              service includes:
            </p>
            <ul>
              <li>News article aggregation from various RSS sources</li>
              <li>AI-generated content and analysis</li>
              <li>Search and filtering capabilities</li>
              <li>User authentication and personalized features</li>
              <li>Research workspace for authenticated users</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>3. User Responsibilities</h2>
            
            <h3>3.1 Acceptable Use</h3>
            <p>You agree to use the Site only for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul>
              <li>Use the Site in any way that violates applicable laws or regulations</li>
              <li>Transmit or procure the sending of any advertising or promotional material</li>
              <li>Impersonate or attempt to impersonate the Company, employees, or other users</li>
              <li>Engage in any other conduct that restricts or inhibits anyone's use of the Site</li>
              <li>Use any automated means to access the Site for any purpose</li>
            </ul>

            <h3>3.2 Account Security</h3>
            <p>
              If you create an account, you are responsible for safeguarding your account 
              credentials and for all activities that occur under your account.
            </p>
          </section>

          <section className={styles.section}>
            <h2>4. Content and Intellectual Property</h2>
            
            <h3>4.1 Our Content</h3>
            <p>
              The Site and its original content, features, and functionality are owned by 
              DarkNews and are protected by international copyright, trademark, patent, 
              trade secret, and other intellectual property laws.
            </p>

            <h3>4.2 Third-Party Content</h3>
            <p>
              Our Site aggregates content from RSS sources. We respect intellectual property
              rights, attribute original sources, and send readers to the publisher via “View
              Original.” Public article pages show the headline and RSS description only; we do
              not reprint full articles. If you are a publisher or rights holder and want a feed
              removed, use our{' '}
              <a href="/copyright">Copyright / DMCA</a> page. We honor valid notices and will
              disable matching RSS feeds on request.
            </p>

            <h3>4.3 User-Generated Content</h3>
            <p>
              If you submit content to our Site, you grant us a non-exclusive, royalty-free, 
              worldwide license to use, modify, and display such content in connection with 
              our services.
            </p>
          </section>

          <section className={styles.section}>
            <h2>5. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Our collection and use of personal information 
              is governed by our Privacy Policy, which is incorporated into these Terms by 
              reference. By using our Site, you consent to the collection and use of information 
              as described in our Privacy Policy.
            </p>
          </section>

          <section className={styles.section}>
            <h2>6. Disclaimers and Limitations</h2>
            
            <h3>6.1 Service Availability</h3>
            <p>
              We strive to provide continuous service availability, but we do not guarantee 
              that the Site will be available at all times. We may experience downtime for 
              maintenance, updates, or technical issues.
            </p>

            <h3>6.2 Content Accuracy</h3>
            <p>
              While we strive to provide accurate and up-to-date information, we cannot 
              guarantee the accuracy, completeness, or timeliness of all content on our Site. 
              Users should verify important information independently.
            </p>

            <h3>6.3 Limitation of Liability</h3>
            <p>
              To the maximum extent permitted by law, DarkNews shall not be liable for 
              any indirect, incidental, special, consequential, or punitive damages, including 
              without limitation, loss of profits, data, use, goodwill, or other intangible 
              losses, resulting from your use of the Site.
            </p>
          </section>

          <section className={styles.section}>
            <h2>7. Advertising and Monetization</h2>
            <p>
              Our Site may display advertisements from third-party advertising networks, 
              including Google AdSense. These advertisements are not endorsements by us, 
              and we are not responsible for the content of such advertisements or the 
              products or services offered by advertisers.
            </p>
          </section>

          <section className={styles.section}>
            <h2>8. Termination</h2>
            <p>
              We may terminate or suspend your access to our Site immediately, without prior 
              notice or liability, for any reason whatsoever, including without limitation 
              if you breach the Terms. Upon termination, your right to use the Site will 
              cease immediately.
            </p>
          </section>

          <section className={styles.section}>
            <h2>9. Changes to Terms</h2>
            <p>
              We reserve the right, at our sole discretion, to modify or replace these Terms 
              at any time. If a revision is material, we will try to provide at least 30 days 
              notice prior to any new terms taking effect.
            </p>
          </section>

          <section className={styles.section}>
            <h2>10. Governing Law</h2>
            <p>
              These Terms shall be interpreted and governed by the laws of the United States, 
              without regard to its conflict of law provisions. Our failure to enforce any 
              right or provision of these Terms will not be considered a waiver of those rights.
            </p>
          </section>

          <section className={styles.section}>
            <h2>11. Dispute Resolution</h2>
            <p>
              Any disputes arising out of or relating to these Terms or the Site shall be 
              resolved through binding arbitration in accordance with the rules of the 
              American Arbitration Association.
            </p>
          </section>

          <section className={styles.section}>
            <h2>12. Severability</h2>
            <p>
              If any provision of these Terms is held to be invalid or unenforceable by a 
              court, the remaining provisions of these Terms will remain in effect.
            </p>
          </section>

          <section className={styles.section}>
            <h2>13. Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className={styles.contactInfo}>
              <p><strong>Copyright / DMCA:</strong> <a href="/copyright">darknews.ai/copyright</a></p>
              <p><strong>Email:</strong> <a href="mailto:legal@darknews.ai">legal@darknews.ai</a> · <a href="mailto:copyright@darknews.ai">copyright@darknews.ai</a></p>
              <p><strong>Website:</strong> <a href="https://darknews.ai">https://darknews.ai</a></p>
              <p><strong>Contact Page:</strong> <a href="/contact">Contact Us</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
